import { ColumnHandleDirective } from '@progress/kendo-angular-grid';
import { effectiveZoom } from './effective-zoom';

/**
 * Kendo grid column resize under the density zoom (GEN-E-100-06).
 *
 * A column's resize handle measures the header with `getBoundingClientRect()` (screen px), adds the
 * pointer's `pageX` delta (screen px) and writes the sum to `column.width`, which the grid renders as
 * local px — and the zoom scales again. So at spacious a 150px column dragged 100px grew 141px and the
 * edge ran ahead of the cursor; at compact every column shrank the moment a drag started. The table
 * width it adjusts alongside is read with `offsetWidth`, which is already local.
 *
 * This wraps the handle's three steps once, for the default (unconstrained) resize mode every
 * `virtual-grid` uses: the header widths it starts from are converted to local px, and the drag delta
 * is divided by the zoom recorded at the start of that drag. Constrained mode (`resizable="constrained"`,
 * or Shift held) measures the neighbouring column as well and is left as Kendo ships it. At comfortable
 * the zoom is 1 and nothing changes.
 */

interface ResizableColumn {
  width: number;
  resizeStartWidth: number;
}

interface ResizeState {
  column: ResizableColumn;
  oldWidth: number;
}

interface ZoomableColumnHandle {
  column: ResizableColumn;
  element: { nativeElement: Element };
  service: { resizedColumns?: ResizeState[] };
  readonly isConstrainedMode: boolean;
  initColumnWidth(): void;
  initState(): void;
  resize(args: { deltaPercent: number }): void;
}

/** The zoom a drag started at, per column; absent when the drag is left to Kendo. */
const dragZoom = new WeakMap<ResizableColumn, number>();

let installed = false;

function zoomOf(handle: ZoomableColumnHandle): number {
  const zoom = effectiveZoom(handle.element?.nativeElement);
  return zoom > 0 && Number.isFinite(zoom) ? zoom : 1;
}

/**
 * Installs the correction on Kendo's `ColumnHandleDirective` once. Returns false (and leaves Kendo
 * alone) if any step it wraps is missing — the spec pins them, so a Kendo upgrade that renames one
 * fails a test rather than silently dropping the fix.
 */
export function installKendoColumnResizeZoomFix(
  proto: ZoomableColumnHandle = ColumnHandleDirective.prototype as unknown as ZoomableColumnHandle
): boolean {
  const isLibrary = proto === (ColumnHandleDirective.prototype as unknown as ZoomableColumnHandle);
  if (installed && isLibrary) {
    return true;
  }
  const { initColumnWidth, initState, resize } = proto;
  if (typeof initColumnWidth !== 'function' || typeof initState !== 'function' || typeof resize !== 'function') {
    return false;
  }

  // First resize on a grid: every column's width is seeded from its header's screen-px width.
  proto.initColumnWidth = function (this: ZoomableColumnHandle) {
    initColumnWidth.call(this);
    const zoom = zoomOf(this);
    if (zoom !== 1 && !this.isConstrainedMode && this.column) {
      this.column.width = this.column.width / zoom;
    }
  };

  // Start of every drag: the width the delta is added to, also measured in screen px.
  proto.initState = function (this: ZoomableColumnHandle) {
    initState.call(this);
    const column = this.column;
    if (!column) {
      return;
    }
    const zoom = zoomOf(this);
    if (zoom === 1 || this.isConstrainedMode) {
      dragZoom.delete(column);
      return;
    }
    dragZoom.set(column, zoom);
    column.resizeStartWidth = column.resizeStartWidth / zoom;
    // The (columnResize) event reports this as oldWidth; keep it in the same px as newWidth.
    const state = this.service?.resizedColumns?.find(s => s.column === column);
    if (state) {
      state.oldWidth = column.resizeStartWidth;
    }
  };

  // Every move: the delta arrives as a percentage of the start width, taken from a screen-px pageX.
  proto.resize = function (this: ZoomableColumnHandle, args: { deltaPercent: number }) {
    const zoom = this.column ? dragZoom.get(this.column) : undefined;
    return resize.call(this, zoom && args ? { ...args, deltaPercent: args.deltaPercent / zoom } : args);
  };

  if (isLibrary) {
    installed = true;
  }
  return true;
}
